import { env as envPublic } from '$env/dynamic/public';
import { Feature, isFeatureEnabled } from './featureFlags';

// ----------------------------
// Currency Formatter
// ----------------------------

export function localizeCurrency(value: number | null | undefined): string | null | undefined {
  if (value === null || value === undefined) return value;
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(value);
}

// ----------------------------
// Date Formatter
// ----------------------------

export function localizeDate(value: string | number | Date, format: Intl.DateTimeFormatOptions): string {
  if (!value && value !== 0) return String(value);
  const date = new Date(value);
  return date.toLocaleDateString('da-DK', format);
}

// ----------------------------
// Balance Normalizer
// ----------------------------

export function normalizeBalance(value: number): number {
  return value < 1 && value >= 0 ? 0 : value;
}

// ----------------------------
// Image Optimizer (Cashback)
// ----------------------------

export function modifyImageUrl(originalUrl: string, { width = 800, quality = 90 }: { width?: number; quality?: number } = {}): string | null {
  const newDomain = 'https://images.loyaltykey.com/cashbackapi';
  if (!originalUrl) return null;

  const baseUrls = [envPublic.PUBLIC_GS_STORAGE_URL || '', envPublic.PUBLIC_STAGING_GS_STORAGE_URL || ''];

  const matchingBase = baseUrls.find((base) => originalUrl.startsWith(base));
  if (matchingBase) {
    const path = originalUrl.replace(matchingBase, '');
    return `${newDomain}${path}?quality=${quality}&width=${width}`;
  }

  return originalUrl;
}

// ----------------------------
// Image Optimizer (Online Shopping)
// ----------------------------

export function modifyOnlineshoppingImageUrl(originalUrl: string, { width = 800, quality = 90 }: { width?: number; quality?: number } = {}): string {
  const newDomain = 'https://images.loyaltykey.com/onlineshopping';
  const baseUrl = envPublic.PUBLIC_ONLINESHOPPING_STORAGE_URL || '';
  if (!originalUrl) return originalUrl;

  const path = originalUrl.replace(baseUrl, '');
  return `${newDomain}${path}?quality=${quality}&width=${width}`;
}

// ----------------------------
// FormData → JSON Parser
// ----------------------------

type JsonFormValue = string | Blob;
type NestedObject = Record<string, unknown> | JsonFormValue[];

/**
 * Converts FormData to a JSON string, supporting nested keys.
 */
export function formToJson(data: FormData): string {
  const method = (object: Record<string, NestedObject>, [keyString, value]: [string, JsonFormValue]): Record<string, NestedObject> => {
    const keys = keyString.replace(/\]/g, '').split('[');
    const baseKey = keys[0];
    const lastValue = value;

    let container: NestedObject = object[baseKey] ?? (isNaN(Number(keys[1])) ? {} : []);
    let segment: any = container;

    for (let i = 1; i < keys.length; i++) {
      const key = keys[i];

      if (i === keys.length - 1) {
        if (Array.isArray(segment)) {
          segment.push(lastValue);
        } else {
          segment[key] = lastValue;
        }
      } else {
        const nextKey = keys[i + 1];
        if (segment[key] === undefined) {
          segment[key] = isNaN(Number(nextKey)) ? {} : [];
        }
        segment = segment[key];
      }
    }

    object[baseKey] = container;
    return object;
  };

  const entries = Array.from(data.entries()) as [string, JsonFormValue][];
  const result = entries.reduce(method, {} as Record<string, NestedObject>);
  return JSON.stringify(result);
}

// ----------------------------
// Google Maps URL Generator
// ----------------------------

export function googleMapsUrl(geoPoint: [number, number]): string {
  const [lng, lat] = geoPoint;
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

// ----------------------------
// Days Remaining Calculator
// ----------------------------

export function calculateDaysRemaining(endDate: string): number {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
}

// ----------------------------
// eShop Link Rewriter
// ----------------------------

export interface NavItem {
  href: string;
  [key: string]: unknown;
}

export function rewriteEshopLinks(items: NavItem[], userExternalId: string): NavItem[] {
  const externalUrl = userExternalId ? `https://eshop.cashbackmedvisa.dk?userID=${userExternalId}` : 'https://eshop.cashbackmedvisa.dk';

  return items.map((item) => {
    if (!item.href.startsWith('/eshop') || isFeatureEnabled(Feature.ESHOP)) {
      return item;
    }

    return {
      ...item,
      href: externalUrl,
    };
  });
}

// ----------------------------
// Safe Redirect Handler
// ----------------------------

export async function getSafeRedirect(requestUrl: string, defaultRedirect: string = '/min-konto'): Promise<string> {
  if (!isFeatureEnabled(Feature.REDIRECT)) return defaultRedirect;

  const { allowedBases } = await import(`$lib/publishers/${envPublic.PUBLIC_PUBLISHER}/whitelistedUrls.js`);

  const url = new URL(requestUrl);
  const encoded = url.searchParams.get('redirectUrl');

  let decoded: string | null = null;
  try {
    decoded = encoded ? decodeURIComponent(encoded) : null;
  } catch {
    decoded = null;
  }

  if (!decoded || !decoded.trim()) return defaultRedirect;

  decoded = decoded.trim();
  const isAllowed = allowedBases.some((base: string) => decoded!.startsWith(base));

  return isAllowed ? decoded : defaultRedirect;
}

// ----------------------------
// SSR WebView/Iframe Detection
// ----------------------------

export const detectWebViewOrIframeSSR = (userAgent: string = ''): boolean => {
  if (!userAgent) return false;

  // iOS WebViews
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    if (!userAgent.includes('Safari') || userAgent.includes('CriOS') || userAgent.includes('GSA')) {
      return true;
    }
  }

  // Android WebViews
  if (/android/i.test(userAgent)) {
    if (userAgent.includes('wv') || userAgent.includes('Version/')) {
      return true;
    }
  }

  return false;
};
